<script lang="ts">
	import { dateSelected } from '#lib/stores/calendarDates.js';
	import { t } from '#lib/stores/i18n.js';
	import {
		advanceTimeBy,
		isTimeSimulated,
		now,
		resetTimeTravel,
		setTimeTravel,
		timeOffsetMs,
		today,
		businessTimezone
	} from '#lib/stores/time.js';
	import { DateTime } from 'luxon';
	import { Calendar, Clock, FastForward, Globe, RotateCcw } from '@lucide/svelte';

	const COMMON_TIMEZONES = [
		'Europe/Paris',
		'UTC',
		'Europe/London',
		'America/New_York',
		'America/Chicago',
		'America/Los_Angeles',
		'America/Montreal',
		'Asia/Tokyo',
		'Asia/Dubai',
		'Australia/Sydney',
		'Pacific/Auckland'
	];

	let customDateTime = $state('');

	function handleApplyCustomDate() {
		if (!customDateTime) return;
		const parsed = DateTime.fromISO(customDateTime, { zone: $businessTimezone });
		if (parsed.isValid) {
			setTimeTravel(parsed);
			dateSelected.set($today);
		}
	}

	function handleJumpToday() {
		resetTimeTravel();
		dateSelected.set($today);
	}

	function handleAdvance(amount: Parameters<typeof advanceTimeBy>[0]) {
		advanceTimeBy(amount);
		dateSelected.set($today);
	}

	function handleReset() {
		resetTimeTravel();
		dateSelected.set($today);
	}

	function formatOffset(offsetMs: number | null): string {
		if (!offsetMs) return '0s';
		const dur = DateTime.now()
			.plus({ milliseconds: offsetMs })
			.diff(DateTime.now(), ['months', 'days', 'hours', 'minutes']);
		const parts: string[] = [];
		if (dur.months) parts.push(`${dur.months > 0 ? '+' : ''}${dur.months}m`);
		if (dur.days) parts.push(`${dur.days > 0 ? '+' : ''}${dur.days}j`);
		if (dur.hours) parts.push(`${dur.hours > 0 ? '+' : ''}${dur.hours}h`);
		if (dur.minutes) parts.push(`${dur.minutes > 0 ? '+' : ''}${dur.minutes}min`);
		return parts.join(' ') || (offsetMs > 0 ? '+1min' : '-1min');
	}
</script>

<div class="space-y-5">
	<!-- Hero State Card -->
	<div
		class="p-5 rounded-2xl border {$isTimeSimulated
			? 'bg-admin/10 border-admin text-surface-on dark:text-white'
			: 'bg-surface-on/3 dark:bg-white/3 border-border/60 dark:border-white/10'}"
	>
		<div class="flex items-start justify-between gap-4 flex-wrap">
			<div>
				<div class="flex items-center gap-2 mb-1.5">
					<Clock
						class="w-5 h-5 {$isTimeSimulated
							? 'text-admin'
							: 'text-surface-on/60 dark:text-slate-400'}"
					/>
					<span
						class="text-xs font-bold uppercase tracking-wider text-surface-on/60 dark:text-slate-400"
					>
						{$isTimeSimulated
							? $t('diagnostics.timetravel.status_simulated')
							: $t('diagnostics.timetravel.status_real')}
					</span>
					{#if $isTimeSimulated}
						<span
							class="px-2 py-0.5 rounded-full text-2xs font-bold bg-admin text-admin-on"
						>
							{formatOffset($timeOffsetMs)}
						</span>
					{/if}
				</div>
				<div
					class="text-2xl sm:text-3xl font-bold font-mono tracking-tight text-surface-on dark:text-white"
				>
					{$now.toFormat('dd/MM/yyyy')}
					<span class="text-admin font-semibold">{$now.toFormat('HH:mm:ss')}</span>
				</div>
				<p class="text-xs text-surface-on/60 dark:text-slate-400 mt-1">
					{$t('diagnostics.timetravel.subtitle')}
				</p>
			</div>

			{#if $isTimeSimulated}
				<button
					type="button"
					class="px-3.5 py-2 rounded-xl text-xs font-semibold bg-admin text-admin-on hover:opacity-90 active:scale-95 transition-all flex items-center gap-1.5 shadow-sm cursor-pointer"
					onclick={handleReset}
				>
					<RotateCcw class="w-3.5 h-3.5" />
					{$t('diagnostics.timetravel.reset')}
				</button>
			{/if}
		</div>
	</div>

	<!-- Quick Jump Actions -->
	<div class="space-y-2">
		<span
			class="text-xs font-bold text-surface-on/70 dark:text-slate-300 uppercase tracking-wider"
		>
			{$t('diagnostics.timetravel.quick_jumps')}
		</span>
		<div class="grid grid-cols-2 sm:grid-cols-5 gap-2">
			<button
				type="button"
				class="p-3 rounded-xl border border-border/70 dark:border-white/10 bg-surface-on/3 dark:bg-white/3 hover:bg-admin/10 hover:border-admin/40 text-surface-on dark:text-slate-200 text-xs font-semibold transition-all flex flex-col items-center gap-1 cursor-pointer"
				onclick={handleJumpToday}
			>
				<Calendar class="w-4 h-4 text-admin" />
				{$t('diagnostics.timetravel.jump_today')}
			</button>
			<button
				type="button"
				class="p-3 rounded-xl border border-border/70 dark:border-white/10 bg-surface-on/3 dark:bg-white/3 hover:bg-admin/10 hover:border-admin/40 text-surface-on dark:text-slate-200 text-xs font-semibold transition-all flex flex-col items-center gap-1 cursor-pointer"
				onclick={() => handleAdvance({ days: 1 })}
			>
				<FastForward class="w-4 h-4 text-admin" />
				{$t('diagnostics.timetravel.jump_1d')}
			</button>
			<button
				type="button"
				class="p-3 rounded-xl border border-border/70 dark:border-white/10 bg-surface-on/3 dark:bg-white/3 hover:bg-admin/10 hover:border-admin/40 text-surface-on dark:text-slate-200 text-xs font-semibold transition-all flex flex-col items-center gap-1 cursor-pointer"
				onclick={() => handleAdvance({ weeks: 1 })}
			>
				<FastForward class="w-4 h-4 text-admin" />
				{$t('diagnostics.timetravel.jump_1w')}
			</button>
			<button
				type="button"
				class="p-3 rounded-xl border border-border/70 dark:border-white/10 bg-surface-on/3 dark:bg-white/3 hover:bg-admin/10 hover:border-admin/40 text-surface-on dark:text-slate-200 text-xs font-semibold transition-all flex flex-col items-center gap-1 cursor-pointer"
				onclick={() => handleAdvance({ months: 1 })}
			>
				<FastForward class="w-4 h-4 text-admin" />
				{$t('diagnostics.timetravel.jump_1m')}
			</button>
			<button
				type="button"
				class="p-3 rounded-xl border border-border/70 dark:border-white/10 bg-surface-on/3 dark:bg-white/3 hover:bg-admin/10 hover:border-admin/40 text-surface-on dark:text-slate-200 text-xs font-semibold transition-all flex flex-col items-center gap-1 cursor-pointer"
				onclick={() => handleAdvance({ months: -1 })}
			>
				<RotateCcw class="w-4 h-4 text-admin" />
				{$t('diagnostics.timetravel.jump_past_1m')}
			</button>
		</div>
	</div>

	<!-- Custom Date & Time + Timezone -->
	<div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
		<!-- Custom DateTime Picker -->
		<div
			class="p-4 rounded-2xl border border-border/60 dark:border-white/10 bg-surface-on/2 dark:bg-white/2 space-y-2"
		>
			<label
				for="custom-time-input"
				class="block text-xs font-bold text-surface-on/70 dark:text-slate-300 uppercase tracking-wider"
			>
				{$t('diagnostics.timetravel.custom_datetime')}
			</label>
			<div class="flex gap-2">
				<input
					id="custom-time-input"
					type="datetime-local"
					bind:value={customDateTime}
					class="flex-1 px-3 py-2 text-xs rounded-xl border border-border/80 dark:border-white/15 bg-surface dark:bg-black text-surface-on dark:text-white focus:outline-hidden focus:border-admin"
				/>
				<button
					type="button"
					class="px-4 py-2 text-xs font-semibold rounded-xl bg-admin text-admin-on hover:opacity-90 active:scale-95 transition-all disabled:opacity-40 cursor-pointer"
					disabled={!customDateTime}
					onclick={handleApplyCustomDate}
				>
					{$t('diagnostics.timetravel.apply')}
				</button>
			</div>
		</div>

		<!-- Timezone Selector -->
		<div
			class="p-4 rounded-2xl border border-border/60 dark:border-white/10 bg-surface-on/2 dark:bg-white/2 space-y-2"
		>
			<label
				for="timezone-select"
				class="block text-xs font-bold text-surface-on/70 dark:text-slate-300 uppercase tracking-wider"
			>
				<span class="inline-flex items-center gap-1">
					<Globe class="w-3.5 h-3.5 text-admin" />
					{$t('diagnostics.timetravel.timezone')}
				</span>
			</label>
			<select
				id="timezone-select"
				bind:value={$businessTimezone}
				class="w-full px-3 py-2 text-xs rounded-xl border border-border/80 dark:border-white/15 bg-surface dark:bg-black text-surface-on dark:text-white focus:outline-hidden focus:border-admin font-mono cursor-pointer"
			>
				{#each COMMON_TIMEZONES as tz}
					<option value={tz}>{tz}</option>
				{/each}
			</select>
		</div>
	</div>
</div>
